import React, { useState, useEffect } from "react";
import * as env from "shared/env";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "redux/store";
import { AuthLayout } from "component/layout/auth";
import * as Router from "react-router-dom";
import { urls } from "routes/urls";
import { login } from "redux/slice/authentication";
import { getUserUnsafe, getAuthError } from "redux/selector";
import { styles } from "./style";
import { getClientById } from "redux/selector"
import queryString from "query-string";
import { getResellerById } from "redux/selector";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { logout } from "redux/slice/authentication";
import { useTheme } from "@mui/material/styles";

const getResellerId = (props: Router.RouteComponentProps) => {
	const query = queryString.parse(props.location.search);
	let result: number | undefined;
	if (typeof query.resellerId === "string") {
		result = parseInt(query.resellerId);
	}
	return result;
}

const getClientId = (props: Router.RouteComponentProps) => {
	const query = queryString.parse(props.location.search);
	let result: number | undefined;
	if (typeof query.clientId === "string") {
		result = parseInt(query.clientId);
	}
	return result;
}

interface Props extends
	Router.RouteComponentProps,
	WithStyles<typeof styles>
{
}

const Component: React.FC<Props> = ({ classes = {}, location, history }) => {
	const dispatch = useDispatch<any>();
	const theme = useTheme();

	// Redux state selectors
	const resellerId = getResellerId({ location } as Router.RouteComponentProps);
	const clientId = getClientId({ location } as Router.RouteComponentProps);

	const user = useSelector(getUserUnsafe);
	const authError = useSelector((state: RootState) => getAuthError(state));
	const reseller = useSelector((state: RootState) => getResellerById(state, resellerId));
	const client = useSelector((state: RootState) => getClientById(state, clientId));

	// Component state
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);

	// Effects
	useEffect(() => {
		if (user) {
			const url = urls.landing(user);
			history.push(url);
		}
	}, [user, history]);

	// Handlers
	const handleLogin = (event: React.FormEvent<HTMLElement>) => {
		event.preventDefault();
		if (username && password) {
			dispatch(login({ username, password }));
		}
	};

	const handleLogout = () => {
		dispatch(logout());
	};

	// Computed values
	const submittable = username.length > 0 && password.length > 0;

	const errorMessage = (() => {
		switch(authError) {
			case "Incorrect credentials":
				return "The username or password you entered is incorrect."
		}
		return null;
	})();

	if (user) {
		return (
			<AuthLayout
				title="Sign In"
			>
				<Mui.Typography>You are already signed in as</Mui.Typography>
				<Mui.Typography>{user.company}</Mui.Typography>
				<Mui.Button
					variant="contained"
					color="primary"
					className={classes.login}
					onClick={() => handleLogout()}
				>
					Sign Out
				</Mui.Button>
				<Mui.Button
					variant="contained"
					color={"primary"}
					className={classes.login}
					component={RouterLinkWrapper}
					to={urls.landing(user)}
				>
					Back to dashboard
				</Mui.Button>
			</AuthLayout>
		);
	} else {
		return (
			<AuthLayout
				title="Sign In"
				header="Welcome back!"
				subtitle={"Enter your details below to sign in."}
			>
				<>
					<form
						name="loginForm"
						noValidate
						className={classes.form}
						onSubmit={(event: React.FormEvent<HTMLElement>) => handleLogin(event)}
					>
						<Mui.TextField
							className={classes.textField}
							label="Username"
							type="username"
							name="username"
							value={username}
							onChange={(event) => setUsername(event.target.value)}
							variant="outlined"
							
							fullWidth
						/>
						<Mui.TextField
							className={classes.textField}
							label="Password"
							type={showPassword ? "text" : "password"}
							name="password"
							value={password}
							onChange={(event) => setPassword(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") {
									handleLogin(event);
								}
							}}
							variant="outlined"
							
							fullWidth
							InputProps={{
								endAdornment: (
									<Mui.InputAdornment position="end">
										<Mui.IconButton onClick={(event) => setShowPassword(!showPassword)}>
											<FeatherIcon>
												{showPassword ? <Icons.Eye strokeWidth={1.5} color={theme.palette.secondary.main}/> : <Icons.EyeOff strokeWidth={1.5} color={theme.palette.secondary.main}/>}
											</FeatherIcon>
										</Mui.IconButton>
									</Mui.InputAdornment>
								)
							}}
							FormHelperTextProps={{
								className: classes.error
							}}
							helperText={errorMessage}

						/>
						<Mui.Link
							className={classes.forgotLink}
							href={env.javaUrl + "/admin/client/login/recover?resellerId=" + (resellerId || 0)}
							target="_blank"
							variant="body2"
						>
							Forgot Password?
						</Mui.Link>
						<Mui.Button
							variant="contained"
							color="primary"
							className={classes.login}
							disabled={!submittable}
							type="submit"
							{...{ fullWidth: true }}
						>
							Sign in
						</Mui.Button>
					</form>
					{(!reseller || reseller.direct) &&
						<div className={classes.createAccountContainer}>
							<>
								<Mui.Typography>Not registered?</Mui.Typography>
								<Mui.Link href="https://www.ihomefinder.com/products/" target="_blank" variant="body2">
									Create an account
									<Icons.ExternalLink size={14} className={classes.externalLinkIcon} />
								</Mui.Link>
							</>
						</div>
					}
				</>
			</AuthLayout>
		);
	}
};

export const LoginPage = withStyles(Router.withRouter(Component), styles);
